import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

type UserRole = "customer" | "driver" | "admin" | "shop";

interface Profile {
  id: string;
  user_id: string;
  full_name: string | null;
  phone: string | null;
  avatar_url: string | null;
  role: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  role: UserRole | null;
  isLoading: boolean;
  signUp: (email: string, password: string, metadata?: { full_name?: string; phone?: string; role?: UserRole }) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    try {
      logger.debug("Fetching profile");
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", userId)
        .single();

      if (error && error.code !== "PGRST116") {
        logger.error("Error fetching profile", { code: error.code });
        return null;
      }
      logger.debug("Profile fetched successfully");
      return data;
    } catch (err) {
      logger.error("Error fetching profile");
      return null;
    }
  };

  /**
   * Fetch user role for UI rendering purposes ONLY.
   * 
   * SECURITY NOTE: This client-side role is NOT a security boundary.
   * All actual access control is enforced server-side via:
   * - RLS policies on all database tables
   * - has_role() RPC function for edge function authorization
   * - get_effective_role() RPC for authoritative role checking
   * 
   * Even if a user manipulates this client-side role, they cannot:
   * - Access data they don't have RLS permission for
   * - Execute admin edge functions without has_role() validation
   * - Bypass any server-side security checks
   */
  const fetchUserRole = async (userId: string): Promise<UserRole | null> => {
    try {
      logger.debug("Fetching user role for UI rendering");
      // Fetch all roles for the user (they may have multiple)
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);

      if (error) {
        logger.error("Error fetching role", { code: error.code });
        return "customer"; // Default to customer
      }
      
      if (!data || data.length === 0) {
        return "customer";
      }
      
      // Client-side priority for UI: admin > shop > driver > customer
      // Note: Server-side get_effective_role() RPC provides authoritative role
      const roles = data.map(r => r.role as UserRole);
      if (roles.includes("admin")) return "admin";
      if (roles.includes("shop")) return "shop";
      if (roles.includes("driver")) return "driver";
      
      logger.debug("Role fetched successfully for UI");
      return roles[0] || "customer";
    } catch (err) {
      logger.error("Error fetching role");
      return "customer";
    }
  };

  const refreshProfile = async () => {
    if (user) {
      const profileData = await fetchProfile(user.id);
      const userRole = await fetchUserRole(user.id);
      setProfile(profileData);
      setRole(userRole);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        logger.debug("Auth state changed", { event });
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Use setTimeout to avoid potential deadlock with Supabase auth
          setTimeout(async () => {
            const [profileData, userRole] = await Promise.all([
              fetchProfile(session.user.id),
              fetchUserRole(session.user.id)
            ]);
            logger.debug("Profile and role loaded");
            setProfile(profileData);
            setRole(userRole);
            setIsLoading(false);
          }, 0);
        } else {
          setProfile(null);
          setRole(null);
          setIsLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      logger.debug("Initial session check", { hasSession: !!session });
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        Promise.all([
          fetchProfile(session.user.id),
          fetchUserRole(session.user.id)
        ]).then(([profileData, userRole]) => {
          logger.debug("Initial profile and role loaded");
          setProfile(profileData);
          setRole(userRole);
          setIsLoading(false);
        });
      } else {
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (
    email: string,
    password: string,
    metadata?: { full_name?: string; phone?: string; role?: UserRole; [key: string]: any }
  ) => {
    try {
      // All signups redirect to the confirm-email page which then routes based on role
      const redirectUrl = `${window.location.origin}/confirm-email`;

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: metadata?.full_name,
            phone: metadata?.phone,
            role: metadata?.role || "customer",
            // Include all additional metadata for drivers/shops
            ...metadata,
          },
        },
      });
      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
        },
      });
      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?mode=reset`,
      });
      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      logger.debug("Signing in user");
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (!error && data.user) {
        logger.debug("Sign in successful");
        // Fetch profile and role immediately after successful sign in
        const [profileData, userRole] = await Promise.all([
          fetchProfile(data.user.id),
          fetchUserRole(data.user.id)
        ]);
        logger.debug("Post-signin profile and role loaded");
        setProfile(profileData);
        setRole(userRole);
      }
      
      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setRole(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        role,
        isLoading,
        signUp,
        signIn,
        signInWithGoogle,
        signOut,
        refreshProfile,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};